import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Users, Target, Shield, TrendingUp, 
  Briefcase, Award, CheckCircle2, Building2, ChevronRight,
  Cpu, Heart, Scale, Lightbulb
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { team } from '../data/mock';

const TeamPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Separate leadership and advisors
  const leadership = team.filter(member => member.role === 'Leadership');
  const advisors = team.filter(member => member.role === 'Advisor');

  // Investment expertise areas
  const expertiseAreas = [
    { icon: Target, title: 'Market Analysis', description: 'Deep Houston market intelligence driving strategic acquisitions' },
    { icon: TrendingUp, title: 'Capital Deployment', description: 'Disciplined underwriting and value-add execution' },
    { icon: Building2, title: 'NOI Optimization', description: 'Operational expertise maximizing property performance' },
    { icon: Shield, title: 'Risk Management', description: 'Institutional discipline protecting investor capital' }
  ];

  // Aligned interests features
  const alignedInterests = [
    { icon: CheckCircle2, text: 'GP co-investment in every deal' },
    { icon: CheckCircle2, text: 'Performance-based compensation' },
    { icon: CheckCircle2, text: 'Transparent reporting and communication' },
    { icon: CheckCircle2, text: 'Long-term partnership approach' }
  ];

  return (
    <>
      <Helmet>
        <title>ASAP Capital | Houston Private Equity Real Estate Team</title>
        <meta 
          name="description" 
          content="Meet ASAP Capital's Houston-based private equity real estate professionals with decades of experience in multifamily and commercial property investing." 
        />
        <meta name="keywords" content="Houston private equity real estate team, real estate investment team, private equity team, commercial real estate experts, multifamily investment team" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-36 pb-28 lg:pt-44 lg:pb-36 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D4AF37]/3 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl mx-auto text-center"
              >
                {/* Pill badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Users className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Our Team</span>
                </motion.div>

                <motion.h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.3] lg:leading-[1.35] mb-6"
                  variants={itemVariants}
                >
                  Meet Our Houston{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Private Equity
                  </span>
                  <br />
                  Real Estate Team
                </motion.h1>

                <motion.p 
                  className="text-base lg:text-lg text-white/55 leading-relaxed mb-12 max-w-2xl mx-auto"
                  variants={itemVariants}
                >
                  Houston-based real estate professionals with decades of combined experience in multifamily acquisitions, 
                  value-add execution, and commercial property investing across the Texas market.
                </motion.p>

                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                      Schedule Consultation
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/about">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm">
                      Our Story
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== LEADERSHIP TEAM ==================== */}
          <section className="py-16 lg:py-24 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Leadership
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Our Leadership Team
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    Experienced real estate investment professionals driving value-add strategy across the Texas market.
                  </motion.p>
                </div>

                {/* Leadership Cards */}
                <div className="grid md:grid-cols-2 gap-8">
                  {leadership.map((member, index) => (
                    <motion.div
                      key={member.id}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl overflow-hidden border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-500 group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                      whileHover={{ y: -8, boxShadow: '0 25px 60px rgba(0,0,0,0.4)' }}
                    >
                      <div className="flex flex-col lg:flex-row h-full">
                        {/* Image */}
                        <div className="lg:w-2/5 h-72 lg:h-auto lg:min-h-[320px] overflow-hidden flex-shrink-0">
                          <img 
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        
                        {/* Content */}
                        <div className="lg:w-3/5 p-6 lg:p-8">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium">
                              {member.title}
                            </span>
                          </div>
                          
                          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                            {member.name}
                          </h3>
                          
                          <p className="text-white/60 text-sm leading-relaxed mb-5">
                            {member.bio}
                          </p>
                          
                          {/* Expertise Tags */}
                          {member.expertise && (
                            <div className="flex flex-wrap gap-2">
                              {member.expertise.map((skill, i) => (
                                <span 
                                  key={i}
                                  className="px-3 py-1.5 rounded-lg bg-white/5 text-white/50 text-xs border border-white/10"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== INVESTMENT EXPERTISE ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Capabilities
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Investment Expertise
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    How our team sources, underwrites, and operates deals with institutional discipline.
                  </motion.p>
                </div>

                {/* Expertise Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {expertiseAreas.map((area, index) => (
                    <motion.div
                      key={area.title}
                      className="bg-[#111D35]/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                        <area.icon className="h-6 w-6 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {area.title}
                      </h3>
                      <p className="text-white/50 text-sm leading-relaxed">
                        {area.description}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGIC ADVISORS ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
              >
                <div className="mb-12">
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Advisory Board
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-4"
                    variants={itemVariants}
                  >
                    Strategic Advisors
                  </motion.h2>
                  <motion.p 
                    className="text-white/50 max-w-2xl"
                    variants={itemVariants}
                  >
                    Governance, technology, and investor stewardship expertise supporting long-term value creation.
                  </motion.p>
                </div>

                {/* Advisors Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                  {advisors.map((member, index) => (
                    <motion.div
                      key={member.id}
                      className="bg-[#111D35]/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 group"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}
                    >
                      {/* Image - centered face framing */}
                      <div className="h-72 overflow-hidden">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          style={{ objectPosition: '50% 25%' }}
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="p-6">
                        <span className="text-[#D4AF37] text-xs font-medium uppercase tracking-wider">
                          {member.title}
                        </span>
                        <h3 className="text-xl font-bold text-white mt-2 mb-3 group-hover:text-[#D4AF37] transition-colors">
                          {member.name}
                        </h3>
                        <p className="text-white/50 text-sm leading-relaxed mb-4">
                          {member.bio}
                        </p>
                        
                        {/* Education if available */}
                        {member.education && (
                          <div className="pt-4 border-t border-white/10">
                            <p className="text-white/40 text-xs">
                              <span className="text-[#D4AF37]">Education:</span> {member.education}
                            </p>
                          </div>
                        )}
                        
                        {/* Expertise Tags */}
                        {member.expertise && (
                          <div className="flex flex-wrap gap-2 mt-4">
                            {member.expertise.slice(0, 3).map((skill, i) => (
                              <span 
                                key={i}
                                className="px-2 py-1 rounded bg-white/5 text-white/40 text-xs"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ALIGNED INTERESTS ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                {/* Content */}
                <div>
                  <motion.span 
                    className="text-[#D4AF37] text-sm font-semibold tracking-wider uppercase mb-3 block"
                    variants={itemVariants}
                  >
                    Partnership
                  </motion.span>
                  <motion.h2 
                    className="text-2xl lg:text-3xl font-bold text-white mb-6"
                    variants={itemVariants}
                  >
                    Aligned Interests
                  </motion.h2>
                  <motion.p 
                    className="text-white/60 leading-relaxed mb-8"
                    variants={itemVariants}
                  >
                    Our team invests alongside our partners in every deal. We believe in performance-based 
                    compensation and long-term partnerships built on transparency and shared success.
                  </motion.p>
                  
                  <motion.div className="space-y-4" variants={itemVariants}>
                    {alignedInterests.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <item.icon className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                        <span className="text-white/70">{item.text}</span>
                      </div>
                    ))}
                  </motion.div>
                </div>

                {/* Visual Card */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-3xl p-8 border border-white/10">
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                        <Scale className="h-10 w-10 text-[#D4AF37]" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">Investor-First Approach</h3>
                      <p className="text-white/50 text-sm leading-relaxed mb-6">
                        "Invest with a team that has navigated multiple market cycles and puts your interests first."
                      </p>
                      <Link to="/contact" className="inline-block">
                        <button className="cta-contact px-6 py-3 h-12 rounded-full text-sm">
                          Schedule Consultation
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </Link>
                    </div>
                  </div>
                  {/* Decorative glow */}
                  <div className="absolute -top-4 -right-4 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" />
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== CTA SECTION ==================== */}
          <section className="py-16 lg:py-24 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={containerVariants}
                className="text-center"
              >
                <motion.h2 
                  className="text-2xl lg:text-3xl font-bold text-white mb-4"
                  variants={itemVariants}
                >
                  Learn More About Our Approach
                </motion.h2>
                <motion.p 
                  className="text-white/50 max-w-xl mx-auto mb-8"
                  variants={itemVariants}
                >
                  Explore our investment strategy and current portfolio of Houston real estate assets.
                </motion.p>
                
                <motion.div 
                  className="flex flex-wrap justify-center gap-4"
                  variants={itemVariants}
                >
                  <Link to="/strategy">
                    <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2">
                      Our Strategy
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/portfolio">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-6 py-3 h-12 rounded-full text-sm flex items-center gap-2">
                      View Portfolio
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TeamPage;
